/**
 * INCSUBForgotPass Component
 * @author govind dindewad
 * 
 * This component handles the "Forgot Password" workflow. It allows users to:
 * - Request an OTP for resetting the password.
 * - Enter and verify the OTP.
 * - Reset the password upon successful OTP verification.
 * 
 * @Features
 * - Mobile number masking for privacy.
 * - Step-by-step flow (request OTP, verify OTP, reset password).
 * - Error handling for network, server, and validation issues.
 * 
 * @Dependencies
 * - `react-toastify` for notifications.
 * - `react-router` for navigation.
 * - `authService` for API requests related to forgot password workflow.
 * 
 * @Props
 * - `e` (event object) - Passed for compatibility with React's event system.
 */

import React, { useState } from 'react';
import './styleForgotPass.css';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { sendOTP, verifyOTP } from '../../../services/authService';
import INCSUBResetOTPPage from '../resetOTP/ResetOTP';
import INCSUBResetPass from '../resetPass/ResetPass';

function INCSUBForgotPass(e) {
    const location = useLocation();
    const navigate = useNavigate();

    // State variables to manage workflow stages
    const [isOTP, setIsOTP] = useState(false); // Track if OTP has been sent
    const [resetPassword, setResetPassword] = useState(); // Track if user is resetting the password
    const [OTP, setOTP] = useState(); // Track the verified OTP
    const [loginInfo, setLoginInfo] = useState({
        username: location.state, // Preloaded mobile number from navigation state
    });

    /**
     * Masks a phone number for privacy.
     * @param {string} phoneNumber - The phone number to be masked.
     * @returns {string} - Masked phone number.
     * @author govind dindewad
     */
    function maskPhoneNumber(phoneNumber) {
        const str = phoneNumber.toString();
        return "********" + str.slice(-2); // Show only the last two digits
    }

    /**
     * Sends an OTP to the user's mobile number for verification.
     * @author govind dindewad
     */
    const handleForgot = async () => {
        try {
            const response = await sendOTP(loginInfo.username); // API call to send OTP
            if (response?.status === 200) {
                toast.success(response.data.message);
                setIsOTP(true); // Move to OTP verification step
            } else if (response?.status === 301) {
                toast.error('Network Error');
            } else if (response?.status === 500) {
                toast.error('Internal Server Error');
            } else if (response?.status === 401) {
                navigate("/login"); // Redirect to login on unauthorized access
            } else {
                toast.success(response?.data.message); // Other success responses
            }
        } catch (err) {
            toast.error("Something Went Wrong"); // Generic error handling
        }
    };

    /**
     * Verifies the OTP entered by the user.
     * @param {string} otp - The OTP entered by the user.
     * @author govind dindewad
     */
    const checkOTP = async (otp) => {
        const data = {
            userMobileNumber: loginInfo.username,
            otp: otp,
        };
        try {
            const response = await verifyOTP(data); // API call to verify OTP
            if (response?.status === 200) {
                setOTP(data.otp); // Save verified OTP
                toast.success(response.data.message);
                setResetPassword(true); // Move to reset password step
            } else if (response?.status === 301) {
                toast.error('Network Error');
            } else if (response?.status === 500) {
                toast.error('Internal Server Error');
            } else if (response?.status === 401) {
                navigate("/login"); // Redirect to login on unauthorized access
            } else {
                toast.success(response?.data.message); // Other success responses
            }
        } catch (err) {
            toast.error("Something Went Wrong"); // Generic error handling
        }
    };

    return (
        <div className='INCSUBForgotPasspage'>
            {
                !isOTP ? (
                    <div className='INCSUBForgotPasspage_container'>
                        <div className='INCSUBForgotPassinfo_container'>
                            <div className='INCSUBForgotPasspage_headerinfo_container'>
                                <div className='INCSUBForgotPasspage_header'>
                                    Forgot Password
                                </div>
                            </div>
                            <div className='INCSUBForgotPassForm_container'>
                                <form className='INCSUBForgotPass_form' onSubmit={(e) => {
                                    e.preventDefault();
                                    handleForgot();
                                }}>
                                    <div className='INCSUBUsercred_container forgot_pass'>
                                        <label>Mobile no.</label>
                                        <div className='input' value={loginInfo.username}>
                                            {loginInfo.username ? maskPhoneNumber(loginInfo.username) : null}
                                        </div>
                                    </div>
                                    <button
                                        type='submit'
                                        className='INCSUBForgotPassForm_submitbutton'>
                                        Send Verification Code
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                ) : (!resetPassword ? (
                    <INCSUBResetOTPPage userName={loginInfo.username} fun={checkOTP} />
                ) : (
                    <INCSUBResetPass 
                        data={{
                            userMobileNumber: loginInfo.username,
                            otp: OTP
                        }} 
                    />
                ))
            }
        </div>
    );
}

export default INCSUBForgotPass;
